import React from 'react'
import { ApolloProvider } from '@apollo/client'
import { CssBaseline, ThemeProvider } from '@material-ui/core'
import { SnackbarProvider } from 'notistack'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { ErrorBoundary } from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { theme } from './theme'
import { AuthenticationProvider } from './components/authentication/AuthenticationProvider'
import { ProfileProvider } from './components/profile/ProfileProvider'
import { AlertProvider } from './components/alert/AlertProvider'
import { client } from './apollo/client'
import { Root } from './components/Root'
import { getDateLocale } from './i18n'
import { ErrorFallback } from './components/error/ErrorFallback'

export function App(): React.ReactElement {
  const { i18n } = useTranslation()
  const { language } = i18n

  return (
    <ErrorBoundary
      fallback={(errorData) => <ErrorFallback {...errorData} />}
      onReset={() => window.location.reload()}
    >
      <ApolloProvider client={client}>
        <AuthenticationProvider>
          <ProfileProvider>
            <HelmetProvider>
              <Helmet titleTemplate="%s - Sweco Enex" />
              <ThemeProvider theme={theme}>
                <MuiPickersUtilsProvider
                  utils={DateFnsUtils}
                  locale={getDateLocale(language)}
                >
                  <SnackbarProvider preventDuplicate>
                    <AlertProvider>
                      <CssBaseline />
                      <BrowserRouter>
                        <Root />
                      </BrowserRouter>
                    </AlertProvider>
                  </SnackbarProvider>
                </MuiPickersUtilsProvider>
              </ThemeProvider>
            </HelmetProvider>
          </ProfileProvider>
        </AuthenticationProvider>
      </ApolloProvider>
    </ErrorBoundary>
  )
}
